/**
 * The area of the canvas that answers for a node (round 10 A1).
 *
 * MEASURED FIRST, because the walkthrough's number and the cause did
 * not match. It reported "39 of 84 nodes do not answer the click"; a
 * hit-test of the live view found 75 of 78 nodes answering at their
 * ORB and only 37 answering at the centre of their bounding BOX --
 * channels 0 of 8, exactly the profile the walk reported.
 *
 * Two things were behind it, and neither is "the node is not
 * clickable":
 *
 *   1. `.constellation-nlab` carried `pointer-events: none`, so the
 *      only part of a node a reader can actually READ was the only
 *      part they could not click. Clicking a node's own name did
 *      nothing, silently.
 *   2. a node's bounding box spans its orb AND its name, so its
 *      geometric centre lands in the GAP between the two -- empty
 *      canvas by construction. The bigger the orb the likelier it
 *      covers that centre anyway, which is why important business
 *      services (r=13) passed and channels (r=8) never did.
 *
 * So the hit area is the union of the orb and the name, computed here
 * and drawn as one transparent rectangle beneath the node's visuals.
 * It closes the gap, makes the name clickable, and gives a small dot
 * on a dark canvas some forgiveness -- without covering canvas the
 * node does not occupy, which would steal clicks from its neighbours.
 */
import { estimateLabelWidth } from "./spacing";

export interface HitBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Width comes from the SAME estimator the label-placement pass uses.
 *
 * This matters more than the arithmetic: the collision pass decides
 * which labels are drawn by asking how wide they are, and a hit area
 * computed from a DIFFERENT estimate reintroduces overlaps that pass
 * believed it had avoided. Measured on the live view, the shared
 * estimator runs a few pixels wide of the rendered glyphs, which is
 * the right direction for both jobs.
 *
 * The line box is 13.6px measured, split around the baseline.
 */
const LABEL_ASCENT = 11;
const LABEL_DESCENT = 3;
/** A little forgiveness around a target eight pixels across. */
const PAD = 3;

export function nodeHitBox(
  p: { x: number; y: number },
  radius: number,
  label: { text: string; dx: number; dy: number; anchor: string } | null,
): HitBox {
  let x0 = p.x - radius;
  let x1 = p.x + radius;
  let y0 = p.y - radius;
  let y1 = p.y + radius;

  if (label) {
    const w = estimateLabelWidth(label.text);
    const tx = p.x + label.dx;
    const ty = p.y + label.dy;
    const lx0 =
      label.anchor === "end" ? tx - w : label.anchor === "middle" ? tx - w / 2 : tx;
    x0 = Math.min(x0, lx0);
    x1 = Math.max(x1, lx0 + w);
    y0 = Math.min(y0, ty - LABEL_ASCENT);
    y1 = Math.max(y1, ty + LABEL_DESCENT);
  }

  return {
    x: x0 - PAD,
    y: y0 - PAD,
    width: x1 - x0 + PAD * 2,
    height: y1 - y0 + PAD * 2,
  };
}

/**
 * Does a point land on the node's hit area?
 *
 * Exists so the rule can be asserted without a browser: jsdom has no
 * layout, so the only honest unit-level claim about a hit area is the
 * geometry it declares.
 */
export function hitBoxContains(box: HitBox, x: number, y: number): boolean {
  return x >= box.x && x <= box.x + box.width && y >= box.y && y <= box.y + box.height;
}

/** The centre of a hit box -- what a bounding-box hit-test aims at. */
export function hitBoxCentre(box: HitBox): { x: number; y: number } {
  return { x: box.x + box.width / 2, y: box.y + box.height / 2 };
}

/**
 * Round 11 C1 -- which node a click at this point belongs to.
 *
 * Hit rectangles are the union of an orb and its name, so neighbours
 * OVERLAP wherever two nodes are drawn close together. SVG resolves an
 * overlap by paint order: the rectangle drawn last wins, whatever the
 * reader was aiming at. The walk measured three of eighty-five that
 * way -- Backup orchestrator selecting SARIE Gateway, Branch Teller
 * Service selecting Customer Information File, Atlassian selecting
 * Okta -- and called it correctly: "works and lies" is worse than
 * "does not work", because a blast radius read confidently for the
 * wrong vendor is a decision taken on somebody else's evidence.
 *
 * Paint order is arbitrary with respect to intent. Distance is not:
 * the node a reader meant is the one whose ORB CENTRE is nearest the
 * point they clicked. Ties break on id, so the same click always
 * resolves the same way rather than following render order.
 *
 * Only candidates whose own box contains the point are considered --
 * a click in empty canvas still selects nothing, which is the
 * behaviour that keeps the scene from grabbing at the reader.
 */
export function nodeAtPoint(
  point: { x: number; y: number },
  candidates: readonly {
    id: string;
    centre: { x: number; y: number };
    box: HitBox;
    /** Round 14 E3 -- the node's drawn shapes, where known. */
    radius?: number;
    label?: HitBox | null;
  }[],
): string | null {
  /* Round 14 E3 -- nearest SHAPE, not nearest orb centre.

     Round 11 measured the orb centre against paint order and was right
     about paint order. It was not right about names: a node's name is
     part of what a reader clicks, and a click on the name of one node
     resolved to the neighbour whose ORB happened to be closer -- 7 of
     233 at 1,000 items, with the cap not in play, 28 of 400 at 5,000.
     So a candidate's distance is to the nearest thing it actually DRAWS
     (its orb, or its name), which is zero for a click on either; the
     orb centre only breaks a tie between two shapes a click is inside,
     and the id breaks the rest so a click always resolves the same way. */
  let best: { id: string; shape: number; d2: number } | null = null;
  for (const c of candidates) {
    const inside =
      point.x >= c.box.x &&
      point.x <= c.box.x + c.box.width &&
      point.y >= c.box.y &&
      point.y <= c.box.y + c.box.height;
    if (!inside) continue;
    const dx = point.x - c.centre.x;
    const dy = point.y - c.centre.y;
    const d2 = dx * dx + dy * dy;
    const shape = shapeDistance(point, c.radius, c.label ?? null, d2);
    if (
      best === null ||
      shape < best.shape ||
      (shape === best.shape && (d2 < best.d2 || (d2 === best.d2 && c.id < best.id)))
    ) {
      best = { id: c.id, shape, d2 };
    }
  }
  return best?.id ?? null;
}

/** Distance from a point to the nearest shape a node draws. */
function shapeDistance(
  point: { x: number; y: number },
  radius: number | undefined,
  label: HitBox | null,
  centreD2: number,
): number {
  // Without shapes, the pre-E3 rule: distance to the orb centre.
  if (radius === undefined) return Math.sqrt(centreD2);
  let best = Math.max(0, Math.sqrt(centreD2) - radius);
  if (label) {
    const lx = Math.max(label.x - point.x, 0, point.x - (label.x + label.width));
    const ly = Math.max(label.y - point.y, 0, point.y - (label.y + label.height));
    best = Math.min(best, Math.sqrt(lx * lx + ly * ly));
  }
  return best;
}

/** Round 14 E3 -- the rectangle a node's name occupies, unpadded. */
export function labelBox(
  p: { x: number; y: number },
  label: { text: string; dx: number; dy: number; anchor: string },
): HitBox {
  const w = estimateLabelWidth(label.text);
  const tx = p.x + label.dx;
  const ty = p.y + label.dy;
  const x =
    label.anchor === "end" ? tx - w : label.anchor === "middle" ? tx - w / 2 : tx;
  return { x, y: ty - LABEL_ASCENT, width: w, height: LABEL_ASCENT + LABEL_DESCENT };
}
